import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { aiAgentsApi } from "../../lib/api";
import {
  Loader2, Save, PhoneIncoming, Play, Square, Clock, CheckCircle2,
  AlertTriangle, Phone, CalendarPlus, MessageSquare, UserPlus, Mail, BookOpen, Send, Zap,
  ListChecks, Plus, Trash2, ChevronUp, ChevronDown, PowerOff,
} from "lucide-react";

const inputCls =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CAPS = [
  ["answerQuestions", "Answer questions from the Knowledge Base", BookOpen],
  ["bookAppointments", "Book appointments (offers 2 open dispatch-board windows, creates an Unassigned job)", CalendarPlus],
  ["textConfirmations", "Text the caller a booking confirmation right after scheduling", Send],
  ["takeMessages", "Take messages (creates a callback Task + Inbox thread)", MessageSquare],
  ["captureLeads", "Capture new leads (creates a Pipeline lead)", UserPlus],
  ["sendEstimates", "Email estimates after verifying the caller + email address", Mail],
];

/** AI Voice Agents → Receptionist tab: inbound AI answering config + recent calls. */
export default function Receptionist({ meta, isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [calls, setCalls] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activating, setActivating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const load = async () => {
    try {
      const [c, cl] = await Promise.all([aiAgentsApi.receptionist(), aiAgentsApi.receptionistCalls()]);
      setCfg(c);
      setCalls(cl);
      setDirty(false);
    } catch {
      toast.error("Failed to load receptionist settings");
    }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>;

  const set = (k, v) => { setCfg((c) => ({ ...c, [k]: v })); setDirty(true); };
  const setCap = (k, v) => { setCfg((c) => ({ ...c, capabilities: { ...c.capabilities, [k]: v } })); setDirty(true); };

  const activate = async () => {
    setActivating(true);
    try {
      const res = await aiAgentsApi.importReceptionistNumber();
      setCfg((c) => ({ ...c, elPhoneNumberId: res.phoneNumberId, importError: res.importError || "", enabled: true }));
      toast.success(
        res.already
          ? "Inbound AI is already active for this number"
          : res.live
          ? "Inbound AI activated — calls to your number now reach the AI receptionist"
          : "Inbound AI activated using your saved settings — it'll take the call the moment the calling platform finishes syncing"
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || "Activation failed";
      setCfg((c) => ({ ...c, importError: msg }));
      toast.error(msg);
    } finally {
      setActivating(false);
    }
  };

  const deactivate = async () => {
    setDeactivating(true);
    try {
      const saved = await aiAgentsApi.deactivateReceptionist();
      setCfg((c) => ({ ...c, ...saved }));
      toast.success("Inbound AI deactivated — calls to your number ring through normally");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivation failed");
    } finally {
      setDeactivating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const voice = (meta?.voices || []).find((v) => v.voiceId === cfg.voiceId);
      const saved = await aiAgentsApi.saveReceptionist({
        mode: cfg.mode, greeting: cfg.greeting, voiceId: cfg.voiceId,
        voiceName: voice?.name || cfg.voiceName || "", hoursStart: cfg.hoursStart,
        hoursEnd: cfg.hoursEnd, days: cfg.days, timezone: cfg.timezone,
        transferNumber: cfg.transferNumber, capabilities: cfg.capabilities,
        intakeQuestions: cfg.intakeQuestions || [],
      });
      setCfg((c) => ({ ...c, ...saved }));
      setDirty(false);
      toast.success("Receptionist settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="receptionist-panel">
      <StatusBanner cfg={cfg} isAdmin={isAdmin} activating={activating} onActivate={activate} deactivating={deactivating} onDeactivate={deactivate} />

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <PhoneIncoming className="h-4 w-4 text-indigo-600" />
          <h3 className="text-[14px] font-semibold text-slate-900">Inbound answering</h3>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Field label="When should the AI pick up?">
            <select data-testid="rec-mode" value={cfg.mode} onChange={(e) => set("mode", e.target.value)} disabled={!isAdmin} className={inputCls}>
              <option value="off">Off — never answers</option>
              <option value="always">Always answers</option>
              <option value="after_hours">After-hours / overflow only</option>
            </select>
          </Field>
          <Field label="Voice">
            <div className="flex items-center gap-2">
              <select data-testid="rec-voice" value={cfg.voiceId || ""} onChange={(e) => set("voiceId", e.target.value)} disabled={!isAdmin} className={`${inputCls} flex-1`}>
                <option value="">Same as default agent</option>
                {(meta?.voices || []).map((v) => <option key={v.voiceId} value={v.voiceId}>{v.name}{v.description ? ` — ${v.description}` : ""}</option>)}
              </select>
              <PreviewBtn voice={(meta?.voices || []).find((v) => v.voiceId === cfg.voiceId)} />
            </div>
          </Field>
          <Field label="Transfer / office number">
            <input data-testid="rec-transfer" value={cfg.transferNumber || ""} onChange={(e) => set("transferNumber", e.target.value)} disabled={!isAdmin} placeholder="+1 (555) 123-4567" className={inputCls} />
          </Field>
        </div>

        <Field label="Greeting (first thing callers hear — {{company_name}} and {{caller_name}} are filled automatically)">
          <textarea data-testid="rec-greeting" value={cfg.greeting || ""} onChange={(e) => set("greeting", e.target.value)} disabled={!isAdmin} rows={2}
            className="w-full px-2.5 py-2 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
        </Field>

        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <Field label="Office hours">
            <div className="flex items-center gap-2">
              <input data-testid="rec-hours-start" type="time" value={cfg.hoursStart} onChange={(e) => set("hoursStart", e.target.value)} disabled={!isAdmin} className={inputCls} />
              <span className="text-slate-400 text-[12px]">to</span>
              <input data-testid="rec-hours-end" type="time" value={cfg.hoursEnd} onChange={(e) => set("hoursEnd", e.target.value)} disabled={!isAdmin} className={inputCls} />
            </div>
          </Field>
          <Field label="Office days">
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d, i) => (
                <button key={d} type="button" data-testid={`rec-day-${i}`} disabled={!isAdmin}
                  onClick={() => set("days", cfg.days.includes(i) ? cfg.days.filter((x) => x !== i) : [...cfg.days, i].sort())}
                  className={`px-2 py-1 rounded-md text-[11.5px] font-semibold border transition-colors ${cfg.days.includes(i) ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  {d}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Timezone">
            <select data-testid="rec-timezone" value={cfg.timezone} onChange={(e) => set("timezone", e.target.value)} disabled={!isAdmin} className={inputCls}>
              {["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"].map((tz) => (
                <option key={tz} value={tz}>{tz.split("/")[1].replace("_", " ")}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">What the receptionist can do</div>
          <div className="space-y-2">
            {CAPS.map(([key, label, Icon]) => (
              <label key={key} className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" data-testid={`rec-cap-${key}`} checked={!!cfg.capabilities[key]} disabled={!isAdmin}
                  onChange={(e) => setCap(key, e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                <Icon className="h-3.5 w-3.5 text-indigo-500" />
                <span className="text-[13px] text-slate-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {isAdmin && (
          <div className="flex justify-end mt-4">
            <Button onClick={save} disabled={!dirty || saving} data-testid="rec-save-btn"
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save receptionist
            </Button>
          </div>
        )}
      </div>

      <IntakeQuestions cfg={cfg} setCfg={setCfg} setDirty={setDirty} isAdmin={isAdmin} save={save} dirty={dirty} saving={saving} />

      <RecentCalls calls={calls} />
    </div>
  );
}

function IntakeQuestions({ cfg, setCfg, setDirty, isAdmin, save, dirty, saving }) {
  const questions = cfg.intakeQuestions || [];

  const setQuestion = (i, val) => {
    setCfg((c) => ({ ...c, intakeQuestions: (c.intakeQuestions || []).map((q, idx) => (idx === i ? val : q)) }));
    setDirty(true);
  };
  const addQuestion = () => {
    setCfg((c) => ({ ...c, intakeQuestions: [...(c.intakeQuestions || []), ""] }));
    setDirty(true);
  };
  const removeQuestion = (i) => {
    setCfg((c) => ({ ...c, intakeQuestions: (c.intakeQuestions || []).filter((_, idx) => idx !== i) }));
    setDirty(true);
  };
  const moveQuestion = (i, dir) => {
    setCfg((c) => {
      const arr = [...(c.intakeQuestions || [])];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return c;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...c, intakeQuestions: arr };
    });
    setDirty(true);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="rec-intake-questions-panel">
      <div className="flex items-center gap-2 mb-1.5">
        <ListChecks className="h-4 w-4 text-indigo-600" />
        <h3 className="text-[14px] font-semibold text-slate-900">Receptionist Intake Questions</h3>
      </div>
      <p className="text-[12.5px] text-slate-500 mb-4">
        The receptionist asks callers these questions, in order, before confirming an appointment booking.
        Answers are saved to the calendar event and the customer's file.
      </p>

      <div className="space-y-2">
        {questions.length === 0 && (
          <div className="text-[12.5px] text-slate-400 italic" data-testid="rec-intake-questions-empty">
            No intake questions yet — the receptionist will just collect a name and the issue.
          </div>
        )}
        {questions.map((q, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`rec-intake-question-row-${i}`}>
            <span className="text-[11px] font-semibold text-slate-400 w-5 shrink-0">{i + 1}.</span>
            <input
              value={q}
              onChange={(e) => setQuestion(i, e.target.value)}
              disabled={!isAdmin}
              placeholder="e.g. What type of system do you have?"
              data-testid={`rec-intake-question-input-${i}`}
              className={inputCls}
            />
            {isAdmin && (
              <div className="flex items-center gap-0.5 shrink-0">
                <button type="button" onClick={() => moveQuestion(i, -1)} disabled={i === 0} data-testid={`rec-intake-question-up-${i}`}
                  className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => moveQuestion(i, 1)} disabled={i === questions.length - 1} data-testid={`rec-intake-question-down-${i}`}
                  className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => removeQuestion(i)} data-testid={`rec-intake-question-remove-${i}`}
                  className="h-7 w-7 rounded-md flex items-center justify-center text-rose-500 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {isAdmin && (
        <div className="flex items-center justify-between mt-3.5">
          <button type="button" onClick={addQuestion} data-testid="rec-intake-question-add"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-indigo-600 hover:text-indigo-700">
            <Plus className="h-3.5 w-3.5" /> Add question
          </button>
          <Button onClick={save} disabled={!dirty || saving} data-testid="rec-intake-questions-save"
            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save receptionist
          </Button>
        </div>
      )}
    </div>
  );
}

function StatusBanner({ cfg, isAdmin, activating, onActivate, deactivating, onDeactivate }) {
  if (!cfg.number) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-100 px-4 py-3" data-testid="rec-no-number">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-[13px] text-black">
          <span className="font-semibold">No company phone number yet.</span> Claim your text number in
          Company Settings → Email &amp; Messaging — that same number also serves as your AI receptionist
          number for inbound voice calls.
        </div>
      </div>
    );
  }
  const on = cfg.mode !== "off";
  const status = activating ? "activating" : cfg.enabled ? "active" : "inactive";
  const canActivate = isAdmin && !!cfg.number && status === "inactive";
  const disabledReason = !cfg.number
    ? "Claim a company text number first (Company Settings → Email & Messaging) — it also serves as your AI receptionist number for inbound voice calls."
    : "";
  return (
    <div className={`rounded-xl border px-4 py-3 ${on ? "border-emerald-200 bg-emerald-50/70" : "border-slate-200 bg-slate-50"}`} data-testid="rec-status-banner">
      <div className="flex items-start gap-3">
        {on ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <Clock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
        <div className="text-[13px] text-slate-800 flex-1">
          <span className="font-semibold">{on ? "AI answering is configured" : "AI answering is off"}</span> for{" "}
          <span className="font-mono font-semibold">{cfg.number}</span>
          {on && <> — mode: <b>{cfg.mode === "always" ? "always answers" : "after-hours only"}</b>{cfg.mode === "after_hours" && <> (office is {cfg.openNow ? "open now" : "closed now"})</>}</>}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-black/5">
        <ActivationStatusPill status={status} />
        {isAdmin && status !== "active" && (
          <Button
            onClick={onActivate}
            disabled={!canActivate}
            title={disabledReason}
            data-testid="rec-activate-btn"
            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-[12.5px] font-semibold h-8 px-3"
          >
            {status === "activating" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
            Activate inbound AI
          </Button>
        )}
        {isAdmin && status === "active" && (
          <Button
            onClick={onDeactivate}
            disabled={deactivating}
            data-testid="rec-deactivate-btn"
            className="bg-white hover:bg-rose-50 border border-rose-200 disabled:opacity-60 text-rose-600 text-[12.5px] font-semibold h-8 px-3"
          >
            {deactivating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PowerOff className="h-3.5 w-3.5 mr-1.5" />}
            Deactivate inbound AI
          </Button>
        )}
      </div>
      {cfg.importError && status === "active" && (
        <div className="text-[12px] text-amber-700 mt-2" data-testid="rec-activate-error">
          Live sync with the calling platform is still catching up ({cfg.importError}) — your settings are saved and active, and will take effect on the line the moment sync completes.
        </div>
      )}
    </div>
  );
}

function ActivationStatusPill({ status }) {
  const map = {
    inactive: { dot: "bg-slate-400", text: "text-slate-500", label: "Inactive" },
    activating: { dot: "bg-amber-500 animate-pulse", text: "text-amber-700", label: "Activating…" },
    active: { dot: "bg-emerald-500", text: "text-emerald-700", label: "Active" },
  };
  const s = map[status] || map.inactive;
  return (
    <div className="inline-flex items-center gap-2" data-testid="rec-activation-status" data-status={status}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      <span className={`text-[12px] font-semibold ${s.text}`}>{s.label}</span>
    </div>
  );
}

function RecentCalls({ calls }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="rec-calls-table">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
        <Phone className="h-4 w-4 text-indigo-600" />
        <h3 className="text-[14px] font-semibold text-slate-900">Recent AI-answered calls</h3>
      </div>
      {calls.length === 0 ? (
        <div className="px-5 py-8 text-center text-[13px] text-slate-400">No inbound AI calls yet.</div>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="px-5 py-2">Caller</th><th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Duration</th><th className="px-3 py-2">Summary</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => (
              <tr key={c.id} className="border-b border-slate-50">
                <td className="px-5 py-2.5 text-[13px] font-medium text-slate-800">{c.customerName || c.callerId || "Unknown"}</td>
                <td className="px-3 py-2.5 text-[12px] text-slate-500">{(c.at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2.5 text-[12px] text-slate-500">{c.durationSec ? `${Math.round(c.durationSec)}s` : "—"}</td>
                <td className="px-3 py-2.5 text-[12px] text-slate-600 max-w-md truncate">{c.summary || (c.status === "in_progress" ? "In progress…" : "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PreviewBtn({ voice }) {
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  useEffect(() => () => ref.current?.pause(), []);
  useEffect(() => { ref.current?.pause(); setPlaying(false); }, [voice?.voiceId]);
  if (!voice?.previewUrl) return null;
  const toggle = () => {
    if (playing) { ref.current?.pause(); setPlaying(false); return; }
    const a = new Audio(voice.previewUrl);
    ref.current = a;
    a.onended = () => setPlaying(false);
    a.play().catch(() => setPlaying(false));
    setPlaying(true);
  };
  return (
    <button type="button" onClick={toggle} data-testid="rec-voice-preview" title="Preview voice"
      className={`h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-md border ${playing ? "border-indigo-300 bg-indigo-50 text-indigo-600" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
      {playing ? <Square className="h-3.5 w-3.5" /> : <Play className="h-4 w-4" />}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
